import React from 'react';
import { SectionContainer } from '@components/SectionComponents/SectionContainer';
import { Grid, Skeleton } from '@mui/material';
import { Text, LazyLoadContainer } from '@web-for-marketing/react-ui';
import { FeatureCardContent } from '@models/section/featureCardSection';
import { useFeatureCards } from '@queries/featureCard';
import { useAtom } from 'jotai';
import { selectedLanguageAtom, isMobileAtom } from '@atoms/appSettings';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { BlogCard1 } from '@components/BlogCards/BlogCard1';
import { SkeletonCard1 } from '@components/BlogCards/SkeletonCard1';
import { getCardLinkPath, getImageAlt, getImagePath, getPillUrl } from '@helpers/resourceHubCardHelper';

interface FeatureCardSectionProps {
    sectionData: FeatureCardContent;
    lazyLoad?: boolean;
}

const styles = {
    cardContainer: {
        marginTop: '2.4rem',
    },
};

export function FeatureCardSection({ sectionData, lazyLoad }: FeatureCardSectionProps): React.ReactElement | null {
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const [isMobile] = useAtom(isMobileAtom);
    const { prefixStringWithBaseUrl } = useStaticValues();
    const { title, cards } = sectionData;
    const { data: fetchedCards, isLoading } = useFeatureCards(cards);

    const cardsToRender = isMobile ? fetchedCards?.slice(0, 1) : fetchedCards;

    return (
        <SectionContainer>
            {!isLoading ? <Text align='left' variant='body1' weight='bold' color='primary' component='h2'>
                {title ?? 'Feature Cards Section'}
            </Text> : <Skeleton variant='text' width='100%' height={28} />}
            <Grid container spacing={{ xs: 2, md: 4 }} css={styles.cardContainer}>
                {isLoading
                    ? Array.from({ length: isMobile ? 1 : 3 }).map((_, index) => (
                        <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={`skeleton-${index}`}>
                            <SkeletonCard1 />
                        </Grid>
                    ))
                    : cardsToRender?.map((card) => {
                        const entityType = cards.find((c) => c.id === card.id)?.contentType;
                        return (
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={card.slug}>
                                <LazyLoadContainer lazyload={lazyLoad}>
                                    <BlogCard1
                                        title={card.title}
                                        linkPath={getCardLinkPath(card, selectedLanguage, entityType)}
                                        image={{
                                            imagePath: getImagePath(card, prefixStringWithBaseUrl, entityType),
                                            imageAlt: getImageAlt(card),
                                        }}
                                        ariaLabel={`Read more about ${card.title}`}
                                        publishedDateUtc={card.publishedDateUtc ?? ''}
                                        pillData={{
                                            name: entityType || '',
                                            link: getPillUrl(selectedLanguage, entityType),
                                        }}
                                        lazyLoad={lazyLoad}
                                        readMinutes={
                                            'readMinutes' in card && card.readMinutes ? card.readMinutes : undefined
                                        }
                                    />
                                </LazyLoadContainer>
                            </Grid>
                        );
                    })}
            </Grid>
        </SectionContainer>
    );
}
